import React, { useState, Component } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PlusIcon, TrashIcon } from 'lucide-react';
import { FormSection, TextField, SelectField, TextAreaField, DisplayField, FileUploadField } from '../components/FormComponents';
import { mockReceives } from '../data/mockData';
// Mock PO phases data
const poPhasesData: Record<string, any[]> = {
  'Phase 1': [{
    id: 'p1-1',
    type: 'Goods',
    product: 'Laptop Pro X',
    description: 'High performance laptops for dev team',
    orderedQty: 3,
    previouslyReceivedQty: 0,
    receivingQty: 3,
    pendingQty: 0,
    rate: 1200,
    lineAmount: 3600
  }, {
    id: 'p1-2',
    type: 'Service',
    product: 'IT Setup Service',
    description: 'Initial configuration and setup',
    orderedQty: 1,
    previouslyReceivedQty: 0,
    receivingQty: 1,
    pendingQty: 0,
    rate: 500,
    lineAmount: 500
  }],
  'Phase 2': [{
    id: 'p2-1',
    type: 'Goods',
    product: 'Laptop Pro X',
    description: 'High performance laptops for dev team',
    orderedQty: 2,
    previouslyReceivedQty: 0,
    receivingQty: 2,
    pendingQty: 0,
    rate: 1200,
    lineAmount: 2400
  }, {
    id: 'p2-2',
    type: 'Works',
    product: 'Network Cabling',
    description: 'Office floor network installation',
    orderedQty: 1,
    previouslyReceivedQty: 0,
    receivingQty: 1,
    pendingQty: 0,
    rate: 1500,
    lineAmount: 1500
  }]
};
export function ReceiveForm() {
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  const mode = id ? 'edit' : 'create';
  const initialData = mode === 'edit' && id ? mockReceives.find(r => r.id === id) : null;
  const [formData, setFormData] = useState<any>(initialData || {
    receiveNo: 'RCV-AUTO-GEN',
    linkedPONo: '',
    deliveryPhase: '',
    vendor: '',
    department: '',
    project: '',
    procurementMethod: '',
    receiveDate: new Date().toISOString().split('T')[0],
    deliveryChallanNo: '',
    remarks: ''
  });
  const [lineItems, setLineItems] = useState<any[]>(initialData?.lineItems?.map(item => ({
    ...item,
    type: item.type || 'Goods',
    description: item.description || ''
  })) || []);
  const [additionalItems, setAdditionalItems] = useState<any[]>([]);
  const handlePhaseChange = (phase: string) => {
    setFormData({
      ...formData,
      deliveryPhase: phase
    });
    if (phase && poPhasesData[phase]) {
      setLineItems(poPhasesData[phase]);
    } else {
      setLineItems([]);
    }
  };
  const handleLineItemChange = (id: string, field: string, value: any) => {
    setLineItems(items => items.map(item => {
      if (item.id === id) {
        const updated = {
          ...item,
          [field]: value
        };
        if (field === 'receivingQty') {
          updated.pendingQty = updated.orderedQty - updated.previouslyReceivedQty - updated.receivingQty;
          updated.lineAmount = updated.receivingQty * updated.rate;
        }
        return updated;
      }
      return item;
    }));
  };
  const handleAdditionalItemChange = (id: string, field: string, value: any) => {
    setAdditionalItems(items => items.map(item => {
      if (item.id === id) {
        const updated = {
          ...item,
          [field]: value
        };
        if (field === 'receivingQty' || field === 'rate') {
          updated.lineAmount = (Number(updated.receivingQty) || 0) * (Number(updated.rate) || 0);
        }
        return updated;
      }
      return item;
    }));
  };
  const addAdditionalItem = () => {
    setAdditionalItems([...additionalItems, {
      id: `add-${Date.now()}`,
      type: 'Goods',
      product: '',
      description: '',
      orderedQty: 0,
      previouslyReceivedQty: 0,
      receivingQty: 1,
      pendingQty: 0,
      rate: 0,
      lineAmount: 0
    }]);
  };
  const removeAdditionalItem = (id: string) => {
    setAdditionalItems(additionalItems.filter(item => item.id !== id));
  };
  const totalReceiveAmount = lineItems.reduce((sum, item) => sum + item.lineAmount, 0) + additionalItems.reduce((sum, item) => sum + item.lineAmount, 0);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    alert(`Receive entry ${mode === 'create' ? 'created' : 'updated'} successfully!`);
    navigate('/receives');
  };
  return <div className="space-y-6 max-w-6xl mx-auto" data-id="element-1800">
      <div className="flex items-center justify-between" data-id="element-1801">
        <div data-id="element-1802">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-1803">
            {mode === 'create' ? 'Create Receive Entry' : 'Edit Receive Entry'}
          </h1>
          <p className="text-sm text-gray-500 mt-1" data-id="element-1804">
            Record goods, works, or services received for a single PO delivery
            phase.
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="bg-white shadow-sm rounded-lg border border-gray-200 p-6 md:p-8" data-id="element-1805">
        <FormSection title="Basic Information" data-id="element-1806">
          <DisplayField label="Receive No" value={formData.receiveNo} data-id="element-1807" />

          {mode === 'edit' ? <DisplayField label="Linked PO No" value={formData.linkedPONo} data-id="element-1808" /> : <SelectField label="Linked PO No" name="linkedPONo" value={formData.linkedPONo} onChange={e => setFormData({
          ...formData,
          linkedPONo: e.target.value,
          vendor: 'TechCorp Solutions',
          department: 'IT-01',
          project: 'PRJ-IT-100',
          procurementMethod: 'Direct Purchase',
          deliveryPhase: ''
        })} required options={[{
          value: 'PO-2023-001',
          label: 'PO-2023-001'
        }]} data-id="element-1809" />}

          {formData.linkedPONo && <SelectField label="PO Delivery Phase" name="deliveryPhase" value={formData.deliveryPhase} onChange={e => handlePhaseChange(e.target.value)} required options={[{
          value: 'Phase 1',
          label: 'Phase 1 — Expected: 2023-11-14'
        }, {
          value: 'Phase 2',
          label: 'Phase 2 — Expected: 2023-12-01'
        }]} data-id="element-1810" />}

          {formData.linkedPONo && formData.deliveryPhase && <div className="md:col-span-2" data-id="element-1811">
              <div className="bg-indigo-50 border-l-4 border-indigo-400 p-3 rounded-r-md" data-id="element-1812">
                <p className="text-sm text-indigo-700" data-id="element-1813">
                  This receive entry is for{' '}
                  <strong data-id="element-1814">{formData.deliveryPhase}</strong> of{' '}
                  <strong data-id="element-1815">{formData.linkedPONo}</strong>. Each delivery phase
                  requires a separate receive entry.
                </p>
              </div>
            </div>}

          <DisplayField label="Vendor" value={formData.vendor || '-'} data-id="element-1816" />
          <DisplayField label="Department" value={formData.department || '-'} data-id="element-1817" />
          <DisplayField label="Project" value={formData.project || '-'} data-id="element-1818" />
          <DisplayField label="Procurement Method" value={formData.procurementMethod || '-'} data-id="element-1819" />

          <TextField label="Receive Date" name="receiveDate" type="date" value={formData.receiveDate} onChange={e => setFormData({
          ...formData,
          receiveDate: e.target.value
        })} required data-id="element-1820" />
          <TextField label="Delivery Challan No" name="deliveryChallanNo" value={formData.deliveryChallanNo} onChange={e => setFormData({
          ...formData,
          deliveryChallanNo: e.target.value
        })} data-id="element-1821" />
        </FormSection>

        <div className="mb-8" data-id="element-1822">
          <h3 className="text-lg font-medium text-gray-900 mb-4 pb-2 border-b border-gray-200" data-id="element-1823">
            PO Line Items
          </h3>
          <div className="overflow-x-auto" data-id="element-1824">
            <table className="min-w-full divide-y divide-gray-200 border border-gray-200 rounded-md" data-id="element-1825">
              <thead className="bg-gray-50" data-id="element-1826">
                <tr data-id="element-1827">
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-1828">
                    Type
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-1829">
                    Product/Asset
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-1830">
                    Description
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-1831">
                    Ordered Qty
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-1832">
                    Prev. Received
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase w-32" data-id="element-1833">
                    Receiving Qty
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-1834">
                    Pending Qty
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-1835">
                    Rate
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-1836">
                    Line Amount
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-1837">
                {lineItems.length === 0 ? <tr data-id="element-1838">
                    <td colSpan={9} className="px-4 py-4 text-center text-sm text-gray-500" data-id="element-1839">
                      Select a PO and Delivery Phase to load line items.
                    </td>
                  </tr> : lineItems.map(item => <tr key={item.id} data-id="element-1840">
                      <td className="px-4 py-2 text-sm text-gray-500" data-id="element-1841">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${item.type === 'Goods' ? 'bg-blue-100 text-blue-800' : item.type === 'Works' ? 'bg-orange-100 text-orange-800' : 'bg-purple-100 text-purple-800'}`} data-id="element-1842">
                          {item.type}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-900" data-id="element-1843">
                        {item.product}
                      </td>
                      <td className="px-4 py-2" data-id="element-1844">
                        <input type="text" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border p-1.5" value={item.description} onChange={e => handleLineItemChange(item.id, 'description', e.target.value)} placeholder="Add description..." data-id="element-1845" />
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-500" data-id="element-1846">
                        {item.orderedQty}
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-500" data-id="element-1847">
                        {item.previouslyReceivedQty}
                      </td>
                      <td className="px-4 py-2" data-id="element-1848">
                        <input type="number" min="0" max={item.orderedQty - item.previouslyReceivedQty} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border p-1.5" value={item.receivingQty} onChange={e => handleLineItemChange(item.id, 'receivingQty', Number(e.target.value))} data-id="element-1849" />
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-500" data-id="element-1850">
                        {item.pendingQty}
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-500" data-id="element-1851">
                        ${item.rate}
                      </td>
                      <td className="px-4 py-2 text-sm font-medium text-gray-900" data-id="element-1852">
                        ${item.lineAmount.toLocaleString()}
                      </td>
                    </tr>)}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mb-8" data-id="element-1853">
          <div className="flex items-center justify-between mb-4 pb-2 border-b border-gray-200" data-id="element-1854">
            <h3 className="text-lg font-medium text-gray-900" data-id="element-1855">
              Additional Item Receive
            </h3>
            <button type="button" onClick={addAdditionalItem} className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-indigo-700 bg-indigo-100 hover:bg-indigo-200" data-id="element-1856">
              <PlusIcon className="h-3.5 w-3.5 mr-1" data-id="element-1857" /> Add Item
            </button>
          </div>

          {additionalItems.length > 0 ? <div className="overflow-x-auto" data-id="element-1858">
              <table className="min-w-full divide-y divide-gray-200 border border-gray-200 rounded-md" data-id="element-1859">
                <thead className="bg-gray-50" data-id="element-1860">
                  <tr data-id="element-1861">
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase w-32" data-id="element-1862">
                      Type
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-1863">
                      Product/Asset
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-1864">
                      Description
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase w-24" data-id="element-1865">
                      Qty
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase w-32" data-id="element-1866">
                      Rate
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase w-32" data-id="element-1867">
                      Amount
                    </th>
                    <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase w-16" data-id="element-1868">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-1869">
                  {additionalItems.map(item => <tr key={item.id} data-id="element-1870">
                      <td className="px-4 py-2" data-id="element-1871">
                        <select className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border p-1.5" value={item.type} onChange={e => handleAdditionalItemChange(item.id, 'type', e.target.value)} data-id="element-1872">
                          <option value="Goods" data-id="element-1873">Goods</option>
                          <option value="Works" data-id="element-1874">Works</option>
                          <option value="Service" data-id="element-1875">Service</option>
                        </select>
                      </td>
                      <td className="px-4 py-2" data-id="element-1876">
                        <input type="text" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border p-1.5" value={item.product} onChange={e => handleAdditionalItemChange(item.id, 'product', e.target.value)} placeholder="Item name" data-id="element-1877" />
                      </td>
                      <td className="px-4 py-2" data-id="element-1878">
                        <input type="text" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border p-1.5" value={item.description} onChange={e => handleAdditionalItemChange(item.id, 'description', e.target.value)} placeholder="Description" data-id="element-1879" />
                      </td>
                      <td className="px-4 py-2" data-id="element-1880">
                        <input type="number" min="1" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border p-1.5" value={item.receivingQty} onChange={e => handleAdditionalItemChange(item.id, 'receivingQty', Number(e.target.value))} data-id="element-1881" />
                      </td>
                      <td className="px-4 py-2" data-id="element-1882">
                        <input type="number" min="0" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border p-1.5" value={item.rate} onChange={e => handleAdditionalItemChange(item.id, 'rate', Number(e.target.value))} data-id="element-1883" />
                      </td>
                      <td className="px-4 py-2 text-sm font-medium text-gray-900" data-id="element-1884">
                        ${item.lineAmount.toLocaleString()}
                      </td>
                      <td className="px-4 py-2 text-center" data-id="element-1885">
                        <button type="button" onClick={() => removeAdditionalItem(item.id)} className="text-red-500 hover:text-red-700" data-id="element-1886">
                          <TrashIcon className="h-4 w-4 mx-auto" data-id="element-1887" />
                        </button>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div> : <p className="text-sm text-gray-500 italic" data-id="element-1888">
              No additional items added.
            </p>}
        </div>

        <div className="mb-8 bg-gray-50 p-4 rounded-md border border-gray-200 flex justify-end items-center" data-id="element-1889">
          <span className="text-sm font-medium text-gray-700 mr-4" data-id="element-1890">
            Total Receive Amount:
          </span>
          <span className="text-xl font-bold text-indigo-600" data-id="element-1891">
            ${totalReceiveAmount.toLocaleString()}
          </span>
        </div>

        <FormSection title="Additional Details" data-id="element-1892">
          <TextAreaField label="Remarks" name="remarks" value={formData.remarks} onChange={e => setFormData({
          ...formData,
          remarks: e.target.value
        })} rows={3} data-id="element-1893" />
          <FileUploadField label="Attachments (Delivery Challan)" data-id="element-1894" />
        </FormSection>

        <div className="mt-8 pt-5 border-t border-gray-200 flex justify-end space-x-3" data-id="element-1895">
          <button type="button" onClick={() => navigate(-1)} className="px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" data-id="element-1896">
            Cancel
          </button>
          <button type="button" className="px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-indigo-700 bg-indigo-50 hover:bg-indigo-100" data-id="element-1897">
            Save as Draft
          </button>
          <button type="submit" className="px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700" data-id="element-1898">
            Submit Receive
          </button>
        </div>
      </form>
    </div>;
}